/* eslint-disable no-console */
import { Injectable, OnDestroy } from '@angular/core';
import * as Sentry from '@sentry/angular';

export interface AudioCaptureResult {
  /** The MediaStream to use for audio capture. Must be obtained beforehand via MicrophoneAccessService. */
  mediaStream: MediaStream;
  /** The AudioWorkletNode for processing audio */
  audioWorkletNode: AudioWorkletNode;
  /** The microphone stream source */
  microphoneStream: MediaStreamAudioSourceNode;
  /** The AudioContext sample rate */
  sampleRate: number;
}

export interface AudioCaptureOptions {
  /** The MediaStream to use for audio capture. Must be obtained beforehand via MicrophoneAccessService. */
  mediaStream: MediaStream;
  /** Callback function to handle audio data from AudioWorklet */
  onAudioData?: (audioBuffer: ArrayBuffer) => void;
}

@Injectable()
export class AudioCaptureService implements OnDestroy {
  private audioContext: AudioContext | undefined;
  private currentCapture: AudioCaptureResult | undefined;

  /**
   * Gets the AudioContext sample rate.
   * @returns The sample rate, or undefined if AudioContext is not initialized
   */
  public getSampleRate(): number | undefined {
    return this.audioContext?.sampleRate;
  }

  /**
   * Gets complete audio capture setup including AudioWorklet.
   *
   * Flow:
   * 1. Cleans up any existing capture to prevent memory leaks
   * 2. Initializes AudioContext if not already created, creates a new one if closed, or awaits resume if suspended
   * 3. Uses provided MediaStream (must be obtained beforehand via MicrophoneAccessService)
   * 4. Loads AudioWorklet processor for real-time audio processing
   * 5. Creates MediaStreamSource from the microphone input
   * 6. Creates AudioWorklet node to process audio chunks
   * 7. Connects: Microphone → AudioWorklet → (callback for audio data)
   *
   * The AudioWorklet processes audio in real-time and sends PCM data
   * via the onAudioData callback.
   *
   * AudioContext is automatically resumed if suspended (awaited to ensure it's running before AudioWorklet operations).
   * Throws an error if resume fails to prevent proceeding with a suspended context.
   *
   * **Memory Safety**: Automatically cleans up any existing capture before creating a new one
   * to prevent memory leaks from rapid start/stop cycles or race conditions.
   *
   * @param options - Configuration options for the audio capture
   * @returns Promise resolving to the capture components
   * @throws Error if setup fails (including AudioContext resume failures)
   */
  public async getAudioCapture(
    options: AudioCaptureOptions,
  ): Promise<AudioCaptureResult> {
    // Clean up any existing capture first to prevent memory leaks
    // This ensures old AudioWorklet nodes and microphone connections are properly released
    // before creating a new capture (important for rapid start/stop cycles or race conditions)
    this.cleanup();

    // Initialize AudioContext if not already created or if it's closed
    if (!this.audioContext || this.audioContext.state === 'closed') {
      this.audioContext = new AudioContext({
        latencyHint: 0.0001,
      });
    } else if (this.audioContext.state === 'suspended') {
      // Resume AudioContext if it's in a suspended state
      await this.audioContext.resume();
    }

    // Load the AudioWorklet processor (modern replacement for ScriptProcessorNode)
    const audioWorkletPath = '/assets/audio-worklet/audio-processor.js';
    await this.audioContext.audioWorklet.addModule(audioWorkletPath);

    const microphoneStream = this.audioContext.createMediaStreamSource(
      options.mediaStream,
    );

    // Create AudioWorklet node
    const audioWorkletNode = new AudioWorkletNode(
      this.audioContext,
      'audio-processor',
    );

    // Handle messages from the AudioWorklet
    audioWorkletNode.port.onmessage = (event) => {
      const { type, data } = event.data as { type: string; data: unknown };

      switch (type) {
        case 'audioProcessingStarted':
          console.log('Audio processing started');
          break;

        case 'audioData':
          if (options.onAudioData) {
            // Type assertion for ArrayBuffer from AudioWorklet
            const audioBuffer = data as ArrayBuffer;

            // Prevent sending empty buffers to avoid unexpected WebSocket closures
            // https://developers.deepgram.com/docs/stt-troubleshooting-websocket-data-and-net-errors
            if (audioBuffer.byteLength === 0) {
              console.warn('Skipping empty audio buffer');
              break;
            }

            options.onAudioData(audioBuffer);
          }
          break;

        case 'audioAnalysis':
          console.log('Audio analysis received data');
          break;

        default:
          console.warn('Unknown message type from AudioWorklet:', type);
      }
    };

    // Connect the audio capture
    microphoneStream.connect(audioWorkletNode);

    const capture: AudioCaptureResult = {
      mediaStream: options.mediaStream,
      audioWorkletNode,
      microphoneStream,
      sampleRate: this.audioContext.sampleRate,
    };

    // Track the current capture
    this.currentCapture = capture;

    return capture;
  }

  /**
   * Tears down the current audio capture and releases all resources.
   * This can be called multiple times safely (idempotent).
   * Use this when you want to clean up the capture but keep the AudioContext open for reuse.
   * AudioContext is automatically suspended to save resources when not capturing.
   */
  public cleanup(): void {
    if (this.currentCapture) {
      this.currentCapture.audioWorkletNode.disconnect();
      this.currentCapture.microphoneStream.disconnect();
      this.currentCapture.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.currentCapture = undefined;

      // Suspend AudioContext to save resources when not capturing
      void this.audioContext?.suspend().catch((error) => {
        console.error('Error suspending AudioContext:', error);
        Sentry.captureException(
          error instanceof Error ? error : new Error(String(error)),
        );
      });
    }
  }

  /**
   * Closes the AudioContext and cleans up all resources.
   * This should be called when the service is being destroyed (e.g., in ngOnDestroy).
   * After calling this, the AudioContext cannot be reused.
   */
  public close(): void {
    // Clean up any active capture first
    this.cleanup();

    // Then close the AudioContext
    if (this.audioContext) {
      void this.audioContext.close();
      this.audioContext = undefined;
    }
  }

  /**
   * Automatically cleans up resources when the service is destroyed.
   */
  ngOnDestroy(): void {
    this.close();
  }
}
